import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { Template } from '../services/TemplateService/Template.type';
import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';
import useSendSlackMessage from './useSubmitFeedback';

const useConsumeGenericGeneratorAsNewPresentation = (tenantId: string) => {
  const metrics = useMetrics();
  const client = useQueryClient();
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { submit } = useSendSlackMessage();
  const presetState = userAccessBadge?.organization?.region;
  const { mutate: consumeGenerator, isLoading, data, isError, error } = useMutation(
    async (params: {
      // Old signature (deprecated)
      prompt?: string;
      grade?: string;
      subject?: string;
      fileType?: string;
      filePath?: string;
      languages?: string[];
      onSuccessFunction?: (template: Template) => void;
    }) => {
      const { prompt, grade, subject, fileType, filePath, languages } = params;

      if (!prompt || !grade) {
        throw new Error('prompt and grade are required when using the old signature');
      }

      metrics.logEvent('Generator.Consume', {
        prompt,
        grade,
        subject,
        generatorId: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
        generatorName: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
        fileType,
        schoolState: presetState,
        languages: languages,
        language: languages?.[0],
        isPresentation: true,
      });
      metrics.logEvent('Generator.Consume.genericFullLesson', {
        prompt,
        grade,
        subject,
        generatorId: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
        generatorName: filePath ? 'genericFullLessonCurify' : 'genericFullLesson',
        fileType,
        schoolState: presetState,
        languages: languages,
        language: languages?.[0],
        isPresentation: true,
      });

      const res = await BackendService.instance.ai.createDynamicLesson(
        {
          prompt,
          grade,
          subject: subject || null,
          fileType: fileType || null,
          filePath: filePath || null,
          languages: languages || ['english'],
          lessonCategory: null,
        },
        tenantId,
      );
      return res;
    },
    {
      onSuccess: (data, { onSuccessFunction }) => {
        client.invalidateQueries(['hasReachedLimit']);

        if (onSuccessFunction && data) {
          onSuccessFunction(data);
        }
        return true;
      },
      cacheTime: 1000 * 60 * 5,

      onError: (error: any) => {
        // Track lesson generation errors

        submit({
          message: `\n*Error consuming full lesson or curify:* ${error.customMessage}\n*Error message:* ${error.message}\n*Error status:* ${error.response?.status}\nError code: ${error.code}`,
          accepted: false,
        });
      },
    },
  );

  return { consumeGenerator, isLoading, isError, data, error: error as any };
};

export default useConsumeGenericGeneratorAsNewPresentation;
